import { useEffect, useMemo, useRef, useState, type FC, type ReactNode } from 'react';
import { Button, Col, Input, InputNumber, Row, Space } from 'antd';
import { ListFilter } from 'lucide-react';
import { TbBuilding, TbFilterPlus, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { type MetricChartCompany } from '@/shared/components/data-visualization/recharts';
import { DropdownOptionContent } from '@/shared/components/dropdowns';
import { type JsonLogicFilter } from '@/shared/components/filters/jsonLogic';
import {
    DEFAULT_FINANCIAL_COMPANIES,
    financialCompanyLabel,
    searchEnrichedFinancialCompanies,
} from '@/shared/components/financial-statements/financialCompanySearch';
import { PlatformForm as Form, PlatformSelect as Select } from '@/shared/components/forms';
import { EmptySection } from '@/shared/components/layouts';
import { TickerExchangeTag } from '@/shared/components/tags';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';
import { FilterExpression, FilterGroup, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { useDatasetSchema } from '../01-Overview/hooks';

interface FiltersProps {
    component: ReportComponent;
}

type SchemaFilter = {
    key: string;
    label?: string;
    type?: string;
    options?: string[];
};

type FilterOperator = '==' | '!=' | '>' | '>=' | '<' | '<=' | 'in' | 'contains';

type FilterRow = {
    id: string;
    field?: string;
    operator: FilterOperator;
    value?: unknown;
};

type CompanyOption = {
    company: MetricChartCompany;
    label: string;
    value: string;
};

const toCompanyOption = (symbol: string): CompanyOption => {
    const company = DEFAULT_FINANCIAL_COMPANIES.find((candidate) => candidate.symbol === symbol) ?? {
        companyName: symbol,
        symbol,
    };

    return { company, label: financialCompanyLabel(company), value: symbol };
};

const COMPANY_FILTER_KEYS = new Set([
    'company',
    'companies',
    'companyid',
    'companyname',
    'investment',
    'investmentid',
    'issuer',
    'organization',
    'symbol',
    'ticker',
]);

const isCompanyFilterKey = (field?: string) =>
    COMPANY_FILTER_KEYS.has((field ?? '').replace(/[_\s-]/g, '').toLowerCase());

const dedupeCompanyOptions = (options: CompanyOption[]) => {
    const seen = new Set<string>();

    return options.filter((option) => {
        if (seen.has(option.value)) {
            return false;
        }

        seen.add(option.value);

        return true;
    });
};

const operatorOptionsByType: Record<string, { label: string; value: FilterOperator }[]> = {
    number: [
        { label: 'Equals', value: '==' },
        { label: 'Does not equal', value: '!=' },
        { label: 'Greater than', value: '>' },
        { label: 'Greater than or equal to', value: '>=' },
        { label: 'Less than', value: '<' },
        { label: 'Less than or equal to', value: '<=' },
        { label: 'Is any of', value: 'in' },
    ],
    enum: [
        { label: 'Equals', value: '==' },
        { label: 'Does not equal', value: '!=' },
        { label: 'Is any of', value: 'in' },
    ],
    string: [
        { label: 'Equals', value: '==' },
        { label: 'Does not equal', value: '!=' },
        { label: 'Contains', value: 'contains' },
        { label: 'Is any of', value: 'in' },
    ],
};

const operatorFromLegacy: Record<string, FilterOperator> = {
    equals: '==',
    not_equals: '!=',
    gt: '>',
    gte: '>=',
    lt: '<',
    lte: '<=',
    in: 'in',
    contains: 'contains',
};

const isLegacyFilterGroup = (filters?: FilterExpression): filters is FilterGroup =>
    Boolean(filters) &&
    typeof filters === 'object' &&
    !Array.isArray(filters) &&
    ('condition' in filters || 'rules' in filters || 'groups' in filters);

const isJsonLogicObject = (value: unknown): value is JsonLogicFilter =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const extractVarName = (value: unknown) => {
    if (!isJsonLogicObject(value)) {
        return undefined;
    }

    return typeof value.var === 'string' ? value.var : undefined;
};

const normalizeFilterValue = (operator: FilterOperator, value: unknown) => {
    if (operator === 'in') {
        return Array.isArray(value) ? value : value === undefined || value === null || value === '' ? [] : [value];
    }

    return Array.isArray(value) ? value[0] : value;
};

const createFilterRow = (field?: string, operator: FilterOperator = '==', value?: unknown, id?: string): FilterRow => ({
    id: id ?? `${field ?? 'filter'}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    field,
    operator,
    value: normalizeFilterValue(operator, value),
});

const jsonLogicToRows = (filters?: FilterExpression): FilterRow[] => {
    if (!filters) {
        return [];
    }

    if (isLegacyFilterGroup(filters)) {
        return (
            filters.rules?.map((rule, index) =>
                createFilterRow(
                    rule.field,
                    operatorFromLegacy[rule.operator] ?? '==',
                    rule.values ?? [],
                    `filter-${index}`,
                ),
            ) ?? []
        );
    }

    if (!isJsonLogicObject(filters)) {
        return [];
    }

    const rows: FilterRow[] = [];

    const walk = (node: JsonLogicFilter) => {
        Object.entries(node).forEach(([operator, rawValue]) => {
            if ((operator === 'and' || operator === 'or') && Array.isArray(rawValue)) {
                rawValue.forEach((child) => {
                    if (isJsonLogicObject(child)) {
                        walk(child);
                    }
                });

                return;
            }

            if (['==', '!=', '>', '>=', '<', '<=', 'in', 'contains'].includes(operator) && Array.isArray(rawValue)) {
                const [left, right] = rawValue;
                const field = extractVarName(left);

                if (field) {
                    rows.push(createFilterRow(field, operator as FilterOperator, right, `filter-${rows.length}`));
                }

                return;
            }

            if (isJsonLogicObject(rawValue)) {
                walk(rawValue);
            }
        });
    };

    walk(filters);

    return rows;
};

const rowsToJsonLogic = (rows: FilterRow[]): JsonLogicFilter | undefined => {
    type ExpressionBucket =
        | { kind: 'single'; expression: JsonLogicFilter }
        | { kind: 'equality'; field: string; expressions: JsonLogicFilter[] };

    const equalityBucketByField = new Map<string, Extract<ExpressionBucket, { kind: 'equality' }>>();
    const expressionBuckets: ExpressionBucket[] = [];

    rows.forEach((row) => {
        if (!row.field) {
            return undefined;
        }

        const value = normalizeFilterValue(row.operator, row.value);

        if (row.operator === 'in' && Array.isArray(value) && !value.length) {
            return undefined;
        }
        if (row.operator !== 'in' && (value === undefined || value === null || value === '')) {
            return undefined;
        }

        const expression = {
            [row.operator]: [{ var: row.field }, value],
        } as JsonLogicFilter;

        if (row.operator === '==') {
            const existingBucket = equalityBucketByField.get(row.field);

            if (existingBucket) {
                existingBucket.expressions.push(expression);
            } else {
                const bucket: Extract<ExpressionBucket, { kind: 'equality' }> = {
                    kind: 'equality',
                    field: row.field,
                    expressions: [expression],
                };

                equalityBucketByField.set(row.field, bucket);
                expressionBuckets.push(bucket);
            }

            return undefined;
        }

        expressionBuckets.push({ kind: 'single', expression });

        return undefined;
    });

    const expressions = expressionBuckets
        .map((bucket) =>
            bucket.kind === 'single'
                ? bucket.expression
                : bucket.expressions.length === 1
                  ? bucket.expressions[0]
                  : ({ or: bucket.expressions } as JsonLogicFilter),
        )
        .filter((expression): expression is JsonLogicFilter => Boolean(expression));

    if (!expressions.length) {
        return undefined;
    }
    if (expressions.length === 1) {
        return expressions[0];
    }

    return {
        and: expressions,
    };
};

const serializeFilters = (filters?: FilterExpression | JsonLogicFilter) => JSON.stringify(filters ?? null);

const getDefaultOperatorForFilter = (filter?: SchemaFilter): FilterOperator => (filter?.type === 'number' ? '>' : '==');

const Filters: FC<FiltersProps> = ({ component }) => {
    const { token } = useAntDesignTheme();
    const { draftConfigurations, replaceDraftConfiguration } = useReportComponentStore();
    const draft: Partial<ReportComponentConfig> = draftConfigurations[component?.id as string];
    const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
    const [companySearchLoading, setCompanySearchLoading] = useState(false);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { schema } = useDatasetSchema(draft as ReportComponentConfig);
    const filterFields = useMemo(() => (schema?.filters ?? []) as SchemaFilter[], [schema?.filters]);
    const fieldByKey = useMemo(() => new Map(filterFields.map((field) => [field.key, field])), [filterFields]);
    const [rows, setRows] = useState<FilterRow[]>(() => jsonLogicToRows(draft?.query?.filters));
    const draftFiltersKey = serializeFilters(draft?.query?.filters);

    useEffect(() => {
        if (serializeFilters(rowsToJsonLogic(rows)) === draftFiltersKey) {
            return;
        }

        setRows(jsonLogicToRows(draft?.query?.filters));
    }, [component.id, draft?.query?.datasetId, draftFiltersKey]);

    const selectedCompanyValues = useMemo(
        () =>
            rows
                .filter((row) => isCompanyFilterKey(row.field))
                .flatMap((row) => (Array.isArray(row.value) ? row.value : [row.value]))
                .filter((value): value is string => typeof value === 'string'),
        [rows],
    );

    const selectedCompanyOptions = useMemo(
        () =>
            selectedCompanyValues.map(
                (symbol) => companyOptions.find((option) => option.value === symbol) ?? toCompanyOption(symbol),
            ),
        [companyOptions, selectedCompanyValues],
    );
    const defaultCompanyOptions = useMemo(() => {
        const schemaSymbols = filterFields.flatMap((field) =>
            isCompanyFilterKey(field.key) ? (field.options ?? []) : [],
        );

        return (schemaSymbols.length ? schemaSymbols : DEFAULT_FINANCIAL_COMPANIES.map(({ symbol }) => symbol)).map(
            toCompanyOption,
        );
    }, [filterFields]);
    const mergedCompanyOptions = useMemo(
        () => dedupeCompanyOptions([...selectedCompanyOptions, ...companyOptions, ...defaultCompanyOptions]),
        [companyOptions, defaultCompanyOptions, selectedCompanyOptions],
    );

    useEffect(() => {
        if (companyOptions.length) {
            return;
        }

        setCompanyOptions(defaultCompanyOptions);
    }, [companyOptions.length, defaultCompanyOptions]);

    const persistRows = (nextRows: FilterRow[]) => {
        const nextFilters = rowsToJsonLogic(nextRows);

        setRows(nextRows);

        if (serializeFilters(nextFilters) === draftFiltersKey) {
            return;
        }

        const baseDraft = draft ?? component.configuration ?? {};

        replaceDraftConfiguration(component.id, {
            ...baseDraft,
            data: undefined,
            query: {
                ...baseDraft?.query,
                datasetId: baseDraft.query?.datasetId ?? '',
                metrics: baseDraft.query?.metrics ?? [],
                dimensions: baseDraft.query?.dimensions ?? [],
                filters: nextFilters,
            },
        });
    };

    const updateRow = (id: string, patch: Partial<FilterRow>) => {
        persistRows(
            rows.map((row) => {
                if (row.id !== id) {
                    return row;
                }

                const nextOperator = patch.operator ?? row.operator;

                return {
                    ...row,
                    ...patch,
                    value:
                        patch.operator && patch.operator !== row.operator
                            ? normalizeFilterValue(nextOperator, row.value)
                            : (patch.value ?? row.value),
                };
            }),
        );
    };

    const addFilter = () => {
        const field = filterFields[0];

        if (!field) {
            return;
        }

        persistRows([...rows, createFilterRow(field.key, getDefaultOperatorForFilter(field))]);
    };

    const searchCompanies = (query: string) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (query.trim().length < 2) {
            setCompanySearchLoading(false);
            setCompanyOptions(dedupeCompanyOptions([...selectedCompanyOptions, ...defaultCompanyOptions]));
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            setCompanySearchLoading(true);

            try {
                const companies = await searchEnrichedFinancialCompanies(query);
                const options = companies.map((company) => ({
                    company,
                    label: financialCompanyLabel(company),
                    value: company.symbol,
                }));

                setCompanyOptions(
                    dedupeCompanyOptions([...selectedCompanyOptions, ...defaultCompanyOptions, ...options]),
                );
            } finally {
                setCompanySearchLoading(false);
            }
        }, 250);
    };

    const renderValueControl = (row: FilterRow) => {
        const field = row.field ? fieldByKey.get(row.field) : undefined;
        const commonProps = {
            size: 'small' as const,
            style: { width: '100%' },
        };
        const selectValue =
            row.operator === 'in'
                ? Array.isArray(row.value)
                    ? row.value.filter((item): item is string => typeof item === 'string')
                    : []
                : typeof row.value === 'string'
                  ? row.value
                  : undefined;

        if (!field) {
            return <Input {...commonProps} disabled placeholder='Select A Field First...' />;
        }

        if (isCompanyFilterKey(field.key)) {
            return (
                <Select
                    {...commonProps}
                    mode={row.operator === 'in' ? 'multiple' : undefined}
                    showSearch
                    filterOption={false}
                    loading={companySearchLoading}
                    optionLabelProp='label'
                    placeholder='Search Companies...'
                    value={selectValue}
                    options={mergedCompanyOptions}
                    optionRender={(option) => {
                        const optionValue = String(option.value ?? '');
                        const company =
                            mergedCompanyOptions.find((candidate) => candidate.value === optionValue)?.company ??
                            toCompanyOption(optionValue).company;
                        const selected = Array.isArray(row.value)
                            ? row.value.includes(optionValue)
                            : row.value === optionValue;
                        const hasExchangeListing = Boolean(company.exchange && company.symbol);

                        return (
                            <DropdownOptionContent
                                avatar={{
                                    icon:
                                        !company.website && !company.image ? (
                                            <TbBuilding size='75%' style={{ color: token.colorTextTertiary }} />
                                        ) : undefined,
                                    src: company.image,
                                    website: company.website,
                                }}
                                metadata={
                                    [
                                        hasExchangeListing ? (
                                            <TickerExchangeTag
                                                companyName={company.companyName}
                                                exchange={company.exchange}
                                                key='exchange'
                                                compact
                                                styles={{ marginLeft: 0 }}
                                                symbol={company.symbol}
                                                website={company.website}
                                            />
                                        ) : null,
                                        company.sector,
                                    ].filter(Boolean) as ReactNode[]
                                }
                                selected={selected}
                                title={company.companyName || optionValue}
                            />
                        );
                    }}
                    onFocus={() => {
                        if (!companyOptions.length) {
                            setCompanyOptions(defaultCompanyOptions);
                        }
                    }}
                    onSearch={searchCompanies}
                    onChange={(value) => updateRow(row.id, { value })}
                    maxTagCount='responsive'
                />
            );
        }

        if (field.options?.length) {
            return (
                <Select
                    {...commonProps}
                    mode={row.operator === 'in' ? 'multiple' : undefined}
                    placeholder='Select Value...'
                    value={selectValue}
                    options={field.options.map((option) => ({ label: option, value: option }))}
                    onChange={(value) => updateRow(row.id, { value })}
                    maxTagCount='responsive'
                />
            );
        }

        if (field.type === 'number') {
            return (
                <InputNumber
                    {...commonProps}
                    value={typeof row.value === 'number' ? row.value : undefined}
                    placeholder='Enter Number...'
                    onChange={(value) => updateRow(row.id, { value })}
                />
            );
        }

        if (row.operator === 'in') {
            return (
                <Select
                    {...commonProps}
                    mode='tags'
                    tokenSeparators={[',']}
                    placeholder='Add Values...'
                    value={Array.isArray(row.value) ? (row.value as string[]) : []}
                    onChange={(value) => updateRow(row.id, { value })}
                    maxTagCount='responsive'
                />
            );
        }

        return (
            <Input
                {...commonProps}
                value={typeof row.value === 'string' || typeof row.value === 'number' ? String(row.value) : undefined}
                placeholder='Enter Value...'
                onChange={(event) => updateRow(row.id, { value: event.target.value })}
            />
        );
    };

    return (
        <div style={{ padding: '0 1rem' }}>
            <Row justify='end' align='middle' style={{ marginBottom: '0.5rem' }}>
                <Col>
                    <Space size={4}>
                        {rows.length !== 0 && (
                            <Button size='small' type='text' icon={<TbX />} onClick={() => persistRows([])}>
                                Clear
                            </Button>
                        )}
                        <Button
                            size='small'
                            type='text'
                            icon={<TbFilterPlus />}
                            onClick={addFilter}
                            disabled={!filterFields.length}
                        >
                            Add
                        </Button>
                    </Space>
                </Col>
            </Row>

            {!filterFields.length ? (
                <EmptySection
                    height='10rem'
                    icon={<ListFilter size='70%' style={{ color: token.colorTextTertiary }} />}
                    label='No Filters Available'
                    description='No filters are available for this dataset.'
                />
            ) : rows.length === 0 ? (
                <EmptySection
                    height='12.5rem'
                    icon={<TbFilterPlus size='75%' style={{ color: token.colorTextTertiary }} />}
                    label={<>Filters Required for Rendering</>}
                    description={<>Add a filter to narrow the data used by this component.</>}
                />
            ) : (
                <Space direction='vertical' size={8} style={{ width: '100%' }}>
                    {rows.map((row) => {
                        const field = row.field ? fieldByKey.get(row.field) : undefined;
                        const operatorOptions =
                            operatorOptionsByType[field?.type ?? 'string'] ?? operatorOptionsByType.string;

                        return (
                            <div
                                key={row.id}
                                style={{
                                    border: `1px solid ${token.colorBorderSecondary}`,
                                    borderRadius: token.borderRadius,
                                    padding: '0.5rem',
                                }}
                            >
                                <Row gutter={8} align='bottom'>
                                    <Col span={7}>
                                        <Form.Item label='Field' style={{ marginBottom: 0 }}>
                                            <Select
                                                size='small'
                                                style={{ width: '100%' }}
                                                value={row.field}
                                                placeholder='Select Field...'
                                                options={filterFields.map((filter) => ({
                                                    label: filter.label ?? filter.key,
                                                    value: filter.key,
                                                }))}
                                                onChange={(fieldKey) => {
                                                    const nextField = fieldByKey.get(fieldKey);

                                                    updateRow(row.id, {
                                                        field: fieldKey,
                                                        operator: getDefaultOperatorForFilter(nextField),
                                                        value: undefined,
                                                    });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item label='Operator' style={{ marginBottom: 0 }}>
                                            <Select
                                                size='small'
                                                style={{ width: '100%' }}
                                                value={row.operator}
                                                options={operatorOptions}
                                                onChange={(operator) => updateRow(row.id, { operator })}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label='Value' style={{ marginBottom: 0 }}>
                                            {renderValueControl(row)}
                                        </Form.Item>
                                    </Col>
                                    <Col span={2}>
                                        <Button
                                            block
                                            size='small'
                                            type='text'
                                            icon={<TbX />}
                                            onClick={() => persistRows(rows.filter((item) => item.id !== row.id))}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}
                </Space>
            )}
        </div>
    );
};

export default Filters;
